import type { AgentRole } from '@/types/api';


export interface AgentRoleMeta {
  role: AgentRole;
  code: string;
  label: string;
  sub: string;
  mode: 'read-only' | 'workspace-write';
  colorVar: string;
}

export const AGENT_ROLES: ReadonlyArray<AgentRoleMeta> = [
  { role: 'explorer', code: 'EX', label: 'Explorer', sub: 'workspace-write · maps the codebase', mode: 'workspace-write', colorVar: 'var(--role-explorer)' },
  { role: 'architect', code: 'AR', label: 'Architect', sub: 'read-only · designs the plan', mode: 'read-only', colorVar: 'var(--role-architect)' },
  { role: 'coder', code: 'CD', label: 'Coder', sub: 'workspace-write · implements', mode: 'workspace-write', colorVar: 'var(--role-coder)' },
  { role: 'reviewer', code: 'RV', label: 'Reviewer', sub: 'read-only · reviews + finds issues', mode: 'read-only', colorVar: 'var(--role-reviewer)' },
  { role: 'tester', code: 'TS', label: 'Tester', sub: 'workspace-write · writes + runs tests', mode: 'workspace-write', colorVar: 'var(--role-tester)' },
  { role: 'documenter', code: 'DC', label: 'Documenter', sub: 'workspace-write · README + docs', mode: 'workspace-write', colorVar: 'var(--role-documenter)' }
];

export const SELECTABLE_MODELS = ['gpt-5.6', 'gpt-5.6-terra', 'gpt-5.6-luna', 'gpt-5.5', 'gpt-5.4'] as const;

export type SelectableModel = (typeof SELECTABLE_MODELS)[number];

export const MODEL_LABELS: Record<string, string> = {
  'gpt-5.6': 'gpt-5.6 (sol)'
};

export const AGENT_MODEL: Record<AgentRole, string> = {
  explorer: 'gpt-5.5',
  architect: 'gpt-5.5',
  coder: 'gpt-5.5',
  reviewer: 'gpt-5.5',
  tester: 'gpt-5.5',
  documenter: 'gpt-5.5'
};

export type ReasoningEffort = 'low' | 'medium' | 'high';

export const DEFAULT_REASONING_EFFORT: Record<AgentRole, ReasoningEffort> = {
  explorer: 'medium',
  architect: 'high',
  coder: 'medium',
  reviewer: 'high',
  tester: 'medium',
  documenter: 'low'
};

export const DEFAULT_PAUSE_AFTER = true;

export const DEFAULT_PAUSE_AFTER_MAP: Record<AgentRole, boolean> = {
  explorer: DEFAULT_PAUSE_AFTER,
  architect: DEFAULT_PAUSE_AFTER,
  coder: DEFAULT_PAUSE_AFTER,
  reviewer: DEFAULT_PAUSE_AFTER,
  tester: DEFAULT_PAUSE_AFTER,
  documenter: DEFAULT_PAUSE_AFTER
};

export function defaultPauseAfterMap(): Record<AgentRole, boolean> {
  return { ...DEFAULT_PAUSE_AFTER_MAP };
}

export const DEFAULT_APPROVAL_SLA_MINUTES = 30;

export const DEFAULT_APPROVAL_SLA_MAP: Record<AgentRole, number> = {
  explorer: DEFAULT_APPROVAL_SLA_MINUTES,
  architect: DEFAULT_APPROVAL_SLA_MINUTES,
  coder: DEFAULT_APPROVAL_SLA_MINUTES,
  reviewer: DEFAULT_APPROVAL_SLA_MINUTES,
  tester: DEFAULT_APPROVAL_SLA_MINUTES,
  documenter: DEFAULT_APPROVAL_SLA_MINUTES
};

export function defaultApprovalSlaMap(): Record<AgentRole, number> {
  return { ...DEFAULT_APPROVAL_SLA_MAP };
}

export interface RetryPolicy {
  max_attempts: number;
  timeout_seconds: number;
}

export const DEFAULT_RETRY_POLICY: Record<AgentRole, RetryPolicy> = {
  explorer: { max_attempts: 2, timeout_seconds: 120 },
  architect: { max_attempts: 2, timeout_seconds: 120 },
  coder: { max_attempts: 3, timeout_seconds: 600 },
  reviewer: { max_attempts: 2, timeout_seconds: 120 },
  tester: { max_attempts: 3, timeout_seconds: 300 },
  documenter: { max_attempts: 3, timeout_seconds: 180 }
};

export interface SandboxMapping {
  sandbox_mode: 'read-only' | 'workspace-write';
  globs: ReadonlyArray<{ key: string; value: string }>;
}

export const SANDBOX_MAPPING: Record<AgentRole, SandboxMapping> = {
  explorer: {
    sandbox_mode: 'workspace-write',
    globs: [{ key: 'sandbox.docs_paths_glob', value: 'docs/requirements/**, docs/functionalities/**' }]
  },
  architect: { sandbox_mode: 'read-only', globs: [] },
  reviewer: { sandbox_mode: 'read-only', globs: [] },
  coder: { sandbox_mode: 'workspace-write', globs: [{ key: 'sandbox.target_files_glob', value: 'src/**' }] },
  tester: {
    sandbox_mode: 'workspace-write',
    globs: [{ key: 'sandbox.test_paths_glob', value: 'tests/**, docs/functionalities/**' }]
  },
  documenter: {
    sandbox_mode: 'workspace-write',
    globs: [
      {
        key: 'sandbox.doc_paths_glob',
        value: 'docs/** (except docs/requirements, docs/functionalities), README.md'
      }
    ]
  }
};

export const MAX_CONFLICT_RETRIES_KEY = 'pipeline.max_conflict_retries';
export const DEFAULT_MAX_CONFLICT_RETRIES = 3;
export const MAX_CONFLICT_RETRIES_MIN = 0;
export const MAX_CONFLICT_RETRIES_MAX = 10;

export function defaultReasoningMap(): Record<AgentRole, ReasoningEffort> {
  return { ...DEFAULT_REASONING_EFFORT };
}

export function defaultRetryMap(): Record<AgentRole, RetryPolicy> {
  return {
    explorer: { ...DEFAULT_RETRY_POLICY.explorer },
    architect: { ...DEFAULT_RETRY_POLICY.architect },
    coder: { ...DEFAULT_RETRY_POLICY.coder },
    reviewer: { ...DEFAULT_RETRY_POLICY.reviewer },
    tester: { ...DEFAULT_RETRY_POLICY.tester },
    documenter: { ...DEFAULT_RETRY_POLICY.documenter }
  };
}
